/**
 * Member application form interactions.
 *
 * Handles three behaviours that Contact Form 7 itself can't express:
 *
 *  1. Toggle between `#einzelbereich` and `#familienbereich` based on the
 *     selected `Beitragsart` radio.
 *  2. Add / remove rows inside `#familienmitglieder` (one input per family
 *     member). Rows are cloned from the LAST existing `[data-member-row]`
 *     so we never get the inconsistent CF7 `<p>`-wrapping on the first row.
 *  3. Auto-label every member row as "Mitglied 1", "Mitglied 2", … and
 *     re-label after every add / remove so the numbering stays correct.
 *
 * The script is idempotent — if the form isn't on the current page the
 * `init` returns early without any side effects.
 */
export function memberApplicationForm() {
	const einzel  = document.getElementById( 'einzelbereich' );
	const familie = document.getElementById( 'familienbereich' );

	// We only initialise if the application form actually lives on this page.
	if ( ! einzel || ! familie ) {
		return;
	}

	const beitragRadios   = document.querySelectorAll( 'input[name="beitragsart"]' );
	const memberContainer = document.getElementById( 'familienmitglieder' );
	const addBtn          = document.getElementById( 'add-member' );

	// -------------------------------------------------------------------------
	// 1. Toggle between „Einzelbeitrag" and „Familienbeitrag" sections
	//
	// Beyond hiding the non-active section visually, we also toggle the HTML5
	// `required` attribute on the scope-conditional fields. Reasoning:
	//
	//   - CF7's `[tag*]`-required-validation runs server-side against ALL
	//     required tags regardless of whether their section is hidden — that's
	//     why we removed `*` from the conditional fields in the shortcode.
	//   - HTML5 `required` enforces client-side: browser blocks the submit if
	//     a visible required field is empty.
	//   - Toggling at runtime means the user sees a native browser error if
	//     they miss a field in the ACTIVE scope, and the hidden scope's
	//     fields never participate.
	// -------------------------------------------------------------------------
	const einzelRequiredFields = [ 'vorname', 'nachname', 'geburtsdatum', 'telefon' ];
	const familieRequiredFields = [ 'telefon-familie' ];

	function setRequired( names, required ) {
		names.forEach( ( name ) => {
			const input = document.querySelector( `[name="${name}"]` );
			if ( ! input ) {
				return;
			}
			if ( required ) {
				input.setAttribute( 'required', 'required' );
			} else {
				input.removeAttribute( 'required' );
			}
		} );
	}

	function applyMode( value ) {
		const isFamily = value === 'Familienbeitrag';

		einzel.style.display  = isFamily ? 'none' : '';
		familie.style.display = isFamily ? ''     : 'none';

		setRequired( einzelRequiredFields,  ! isFamily );
		setRequired( familieRequiredFields, isFamily );
	}

	beitragRadios.forEach( ( radio ) => {
		radio.addEventListener( 'change', () => applyMode( radio.value ) );
		if ( radio.checked ) {
			applyMode( radio.value );
		}
	} );

	// Family-area-specific behaviour (only wire up if those nodes exist).
	if ( ! memberContainer ) {
		return;
	}

	// -------------------------------------------------------------------------
	// 2. Re-label every member row in the container
	// -------------------------------------------------------------------------
	function relabel() {
		const rows = memberContainer.querySelectorAll( '[data-member-row]' );
		rows.forEach( ( row, index ) => {
			const label = row.querySelector( '[data-member-label]' );
			if ( label ) {
				label.textContent = 'Mitglied ' + ( index + 1 );
			}
		} );
	}

	// -------------------------------------------------------------------------
	// 3. Add member — clone the last row, clear the input, re-label
	// -------------------------------------------------------------------------
	if ( addBtn ) {
		addBtn.addEventListener( 'click', () => {
			const rows = memberContainer.querySelectorAll( '[data-member-row]' );
			if ( rows.length === 0 ) {
				return;
			}

			const last  = rows[ rows.length - 1 ];
			const clone = last.cloneNode( true );

			// Reset ALL inputs in the cloned row — cloneNode copies values, so
			// we need to clear every text/date/email/tel field plus uncheck any
			// checkboxes/radios. Otherwise a new family-member row would
			// inherit the previous member's name AND birthdate.
			const inputs = clone.querySelectorAll( 'input, textarea, select' );
			inputs.forEach( ( field ) => {
				if ( field.type === 'checkbox' || field.type === 'radio' ) {
					field.checked = false;
				} else {
					field.value = '';
				}
			} );

			memberContainer.appendChild( clone );
			relabel();

			// Focus the first text-like input on the new row for nicer UX.
			const firstField = clone.querySelector( 'input:not([type="button"]):not([type="hidden"]), textarea, select' );
			if ( firstField ) {
				firstField.focus();
			}
		} );
	}

	// -------------------------------------------------------------------------
	// 4. Remove member — event delegation, keep at least one row
	// -------------------------------------------------------------------------
	memberContainer.addEventListener( 'click', ( event ) => {
		const btn = event.target.closest( '.remove-member' );
		if ( ! btn ) {
			return;
		}

		const row = btn.closest( '[data-member-row]' );
		if ( ! row ) {
			return;
		}

		const rows = memberContainer.querySelectorAll( '[data-member-row]' );
		if ( rows.length <= 1 ) {
			return; // never remove the last remaining row
		}

		row.remove();
		relabel();
	} );

	// Set the initial labels (in case the markup ships without numbers).
	relabel();
}
